// AIChatBot — container / orchestrator for the "Ask Dan" AI travel assistant.
//
// Behavior-preserving decomposition of the original 907 LOC component
// (oversized-component-refactor, issue #240, task 8.7). This file is now a thin
// shell that wires the extracted hooks to the extracted memoized presentation
// components. It keeps the SAME module path, the SAME default export
// (`AIChatBot`), the SAME props (`{ isOpen, onClose, tripData, userProfile }`),
// the SAME `isOpen` gate (returns `null` when closed), and renders the SAME DOM
// (full-screen overlay → modal → header / message list / input area + the mic
// pulse keyframes) as the source — so the 8.1 characterization snapshots match
// exactly.
//
// Layers (each its own file, all ≤400 LOC):
//   logic/         pure input-only helpers (welcome message, trip context,
//                  hotel-flow state machine, search-param + result builders,
//                  placeholder + time formatting)
//   hooks/         useChatSession (send/receive/history + state + effects),
//                  useScrollAnchor (auto-scroll), useVoiceInput (Web Speech API)
//   presentation/  ChatHeader, ChatMessageList (+ ChatMessageBubble, QuickReplies,
//                  MessageCards), ChatInputArea — all React.memo, render-only
//
// Hook composition order is chosen to preserve the source effect sequence:
//   1. useChatSession  → welcome / reset / focus effects
//   2. useScrollAnchor → auto-scroll effect
//   3. useVoiceInput   → Web Speech API setup effect
//
// Validates: Requirements 1.1, 1.2, 2.1, 2.2, 2.3

import React from 'react';

import { useChatSession } from './aiChatBot/hooks/useChatSession';
import { useScrollAnchor } from './aiChatBot/hooks/useScrollAnchor';
import { useVoiceInput } from './aiChatBot/hooks/useVoiceInput';

import ChatHeader from './aiChatBot/presentation/ChatHeader';
import ChatMessageList from './aiChatBot/presentation/ChatMessageList';
import ChatInputArea from './aiChatBot/presentation/ChatInputArea';

const AIChatBot = ({ isOpen, onClose, tripData, userProfile }) => {
  // Chat conversation state + send/receive/history flow.
  const session = useChatSession({ isOpen, tripData, userProfile });
  // Auto-scroll the message list as it changes.
  const { messagesEndRef } = useScrollAnchor(session.messages);
  // Optional voice-to-text input for the composer.
  const { isListening, toggleListening } = useVoiceInput(session.setInputMessage);

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '500px',
        height: '600px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Header */}
        <ChatHeader onClose={onClose} />

        {/* Messages */}
        <ChatMessageList
          messages={session.messages}
          isLoading={session.isLoading}
          onQuickReply={session.handleQuickReply}
          tripData={tripData}
          formatTime={session.formatTime}
          messagesEndRef={messagesEndRef}
        />

        {/* Input */}
        <ChatInputArea
          inputRef={session.inputRef}
          inputMessage={session.inputMessage}
          setInputMessage={session.setInputMessage}
          handleKeyPress={session.handleKeyPress}
          inputPlaceholder={session.inputPlaceholder}
          isLoading={session.isLoading}
          isListening={isListening}
          toggleListening={toggleListening}
          handleSendMessage={session.handleSendMessage}
        />
      </div>

      {/* Pulse animation for mic button */}
      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
        }
      `}</style>
    </div>
  );
};

export default AIChatBot;
